import api from './api'
import type {
  AvaliacaoElegibilidadeProduto,
  AvaliarElegibilidadeRequest,
  AvaliarElegibilidadeResponse,
} from '@/types/elegibilidade'
import type {
  AlterarRotaRequest,
  AtualizarDadosElegibilidadeRequest,
  ElegibilidadeReserva,
  FilaGrandesOperacoes,
  SobreporImpedimentoRequest,
} from '@/types/elegibilidadeReserva'
import type { StatusReserva } from '@/types/statusReserva'

const BASE_URL = '/api/v1/oportunidades'

// ─────────────────────────────────────────────────────────────────────────────────────────
// P2 (AN-PP2 §5.3) — elegibilidade da reserva, rota e fila de Grandes Operações.
//
// Formas conferidas contra o controller real (`ElegibilidadeReservaController`) na integração.
// Nada aqui calcula elegibilidade — a tela mostra o que o servidor devolve. Erros: `RequisicaoFalhou` do interceptor
// (`e.data.errorCode`, `e.data.data` com o detalhe).
// ─────────────────────────────────────────────────────────────────────────────────────────

export const elegibilidadeReservaService = {
  /**
   * `POST /v1/oportunidades/elegibilidade/avaliar` (CONSULTOR+) — não persiste. Sem `produtoId`
   * avalia todos os produtos projetados utilizáveis (uma chamada para os chips do seletor).
   * Resposta `{aviso, avaliacoes:[...]}` — devolve só a lista.
   */
  avaliar: async (request: AvaliarElegibilidadeRequest): Promise<AvaliacaoElegibilidadeProduto[]> => {
    const r = (await api.post(`${BASE_URL}/elegibilidade/avaliar`, request)) as unknown as
      | AvaliarElegibilidadeResponse
      | null
    return r?.avaliacoes ?? []
  },

  /** `GET /v1/oportunidades/{id}/elegibilidade` (CONSULTOR+, escopo de dono → 404). */
  obter: async (id: string): Promise<ElegibilidadeReserva> =>
    (await api.get(`${BASE_URL}/${id}/elegibilidade`)) as unknown as ElegibilidadeReserva,

  /**
   * `PUT /v1/oportunidades/{id}/elegibilidade/dados` — consultor (dono, antes de
   * EM_ESTRUTURACAO) ou back-office (até o primeiro encaminhamento). Origem e autor vêm do token.
   * SUBSTITUI o conjunto declarado: o corpo leva SEMPRE os quatro campos (nulo = apaga).
   * 409 `ELEGIBILIDADE_CONGELADA` depois de encaminhada; 409 `DADOS_ELEGIBILIDADE_NAO_EDITAVEIS`
   * para o consultor a partir de EM_ESTRUTURACAO.
   */
  atualizarDados: async (id: string, dados: AtualizarDadosElegibilidadeRequest): Promise<ElegibilidadeReserva> =>
    (await api.put(`${BASE_URL}/${id}/elegibilidade/dados`, dados)) as unknown as ElegibilidadeReserva,

  /** `POST /v1/oportunidades/{id}/elegibilidade/reavaliar` — back-office (consultor 403). */
  reavaliar: async (id: string): Promise<ElegibilidadeReserva> =>
    (await api.post(`${BASE_URL}/${id}/elegibilidade/reavaliar`, {})) as unknown as ElegibilidadeReserva,

  /**
   * `POST /v1/oportunidades/{id}/elegibilidade/sobreposicoes` — ADMIN. 422 `MOTIVO_OBRIGATORIO`;
   * 409 `AVALIACAO_MUDOU` (o ADMIN sobrepõe exatamente o que viu); 409 `ELEGIBILIDADE_SEM_IMPEDIMENTO`.
   */
  sobrepor: async (id: string, request: SobreporImpedimentoRequest): Promise<ElegibilidadeReserva> =>
    (await api.post(`${BASE_URL}/${id}/elegibilidade/sobreposicoes`, request)) as unknown as ElegibilidadeReserva,

  /** `POST /v1/oportunidades/{id}/rota` — ADMIN (Q67). 422 `ROTA_NAO_APLICAVEL`; terminal → 409. */
  alterarRota: async (id: string, request: AlterarRotaRequest): Promise<ElegibilidadeReserva> =>
    (await api.post(`${BASE_URL}/${id}/rota`, request)) as unknown as ElegibilidadeReserva,

  /**
   * `GET /v1/oportunidades/fila/grandes-operacoes?status=&page=` — back-office (CONSULTOR 403).
   * Mais antigas primeiro. Resposta `{total, page, size, reservas}` (não é PageResponse).
   */
  filaGrandesOperacoes: async (
    opcoes: { status?: StatusReserva | null; page?: number; size?: number } = {}
  ): Promise<FilaGrandesOperacoes> => {
    const params = new URLSearchParams()
    if (opcoes.status) params.set('status', opcoes.status)
    params.set('page', String(opcoes.page ?? 0))
    if (opcoes.size) params.set('size', String(opcoes.size))
    return (await api.get(`${BASE_URL}/fila/grandes-operacoes?${params.toString()}`)) as unknown as FilaGrandesOperacoes
  },
}
